import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

import { SERVER_URL } from "@/config/config";
import { CarsList } from "@/constants/cars";
import { CarLabel } from "@/constants/enum/garage";
import {
  mintNewCars,
  repairCars,
  sellCars,
  shipCars,
} from "@/helper/contractFunctions/MafiaInventory";
import { InventoryCar } from "@/types/CarInventoryType";

export interface GarageState {
  isLoadingCarAction: boolean;
  isLoadingGetGarageList: boolean;
  myGarageList: InventoryCar[];
  garageListTotalCount: number;
  garageListCurrentPage: number;
  garageListPageSize: number;
}

const initialState: GarageState = {
  isLoadingCarAction: false,
  isLoadingGetGarageList: false,
  myGarageList: [],
  garageListTotalCount: 0,
  garageListCurrentPage: 0,
  garageListPageSize: 50,
};

export const getGarageListAction = createAsyncThunk(
  "garage/getGarageListAction",
  async ({
    account,
    from,
    count,
    sortBy,
    sortOrder,
    cityId,
  }: {
    account: string;
    from?: number;
    count?: number;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    cityId?: number;
  }) => {
    let url = `${SERVER_URL}/profile/carNick/garage?address=${account}`;
    if (from !== undefined) url += `&from=${from}`;
    if (count !== undefined) url += `&count=${count}`;
    if (sortBy) url += `&sortBy=${sortBy}`;
    if (sortOrder) url += `&sortOrder=${sortOrder}`;
    if (cityId !== undefined) url += `&cityId=${cityId}`;

    const { data } = await axios.get(url);
    return data;
  }
);

export const bulkCarRepairAction = createAsyncThunk(
  "garage/bulkCarRepairAction",
  async ({ itemIds, account }: { itemIds: number[]; account: string }) =>
    (await repairCars(account, itemIds)).transactionHash
);

export const bulkCarSellAction = createAsyncThunk(
  "garage/bulkCarSellAction",
  async ({ itemIds, account }: { itemIds: number[]; account: string }) =>
    (await sellCars(account, itemIds)).transactionHash
);

export const mintNewCarAction = createAsyncThunk(
  "garage/mintNewCarAction",
  async ({
    account,
    itemIds,
    signMsg,
    signature,
  }: {
    account: string;
    itemIds: number[];
    signMsg: string;
    signature: string;
  }) => (await mintNewCars(account, itemIds, signMsg, signature)).transactionHash
);

export const carShipAction = createAsyncThunk(
  "garage/carShipAction",
  async ({
    itemIds,
    account,
    cityId,
  }: {
    itemIds: number[];
    account: string;
    cityId: number;
  }) => (await shipCars(account, itemIds, cityId)).transactionHash
);

export const changeCarLabelAction = createAsyncThunk(
  "garage/changeCarLabelAction",
  async ({
    address,
    carId,
    label,
  }: {
    address: string;
    carId: number;
    label: CarLabel;
  }) => {
    const { data } = await axios.put(`${SERVER_URL}/profile/carNick/label`, {
      address,
      inventoryId: carId,
      label,
    });
    return data;
  }
);

const garageSlice = createSlice({
  name: "garage",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    const carActions = [
      bulkCarRepairAction,
      bulkCarSellAction,
      mintNewCarAction,
      carShipAction,
    ] as const;

    carActions.forEach((action) => {
      builder
        .addCase(action.pending, (state) => {
          state.isLoadingCarAction = true;
        })
        .addCase(action.fulfilled, (state) => {
          state.isLoadingCarAction = false;
        })
        .addCase(action.rejected, (state) => {
          state.isLoadingCarAction = false;
        });
    });

    builder
      .addCase(getGarageListAction.pending, (state) => {
        state.isLoadingGetGarageList = true;
      })
      .addCase(getGarageListAction.fulfilled, (state, { payload, meta }) => {
        state.isLoadingGetGarageList = false;

        const dataArray = Array.isArray(payload)
          ? payload
          : payload.data || payload.items || [];
        const pagination = payload.pagination || {};
        const totalCount =
          pagination.total !== undefined
            ? pagination.total
            : payload.total !== undefined
              ? payload.total
              : payload.count !== undefined
                ? payload.count
                : dataArray.length;

        state.myGarageList = dataArray
          .filter((item: any) => !!item.Car)
          .map((item: any) => {
            const carInfo = CarsList.find((car) => car.id === item.Car.carType);
            return {
              id: item.id,
              userId: item.user_id,
              address: item.address,
              itemId: item.item_id,
              city: item.city,
              removed: item.removed,
              status: item.status,
              timestamp: item.timestamp,
              car: {
                carType: item.Car.carType,
                damagePercent: item.Car.damagePercent,
                timestamp: item.Car.timestamp,
                block: item.Car.block,
                label: item.Car.label,
                value:
                  ((carInfo?.basePrice || 0) *
                    (100 - item.Car.damagePercent)) /
                  100,
              },
              carType: carInfo,
              basePrice: carInfo?.basePrice || 0,
            };
          }) as InventoryCar[];

        state.garageListTotalCount = totalCount;
        const from = pagination.from ?? meta.arg.from;
        const count = pagination.count ?? meta.arg.count;
        if (from !== undefined) {
          state.garageListCurrentPage = Math.floor(from / (count || 10));
        }
        if (count !== undefined) state.garageListPageSize = count;
      })
      .addCase(getGarageListAction.rejected, (state) => {
        state.isLoadingGetGarageList = false;
      })
      .addCase(changeCarLabelAction.pending, (state) => {
        state.isLoadingCarAction = true;
      })
      .addCase(changeCarLabelAction.fulfilled, (state, { meta }) => {
        state.isLoadingCarAction = false;
        state.myGarageList = state.myGarageList.map((car) =>
          car.id === meta.arg.carId
            ? { ...car, car: { ...car.car, label: meta.arg.label } }
            : car
        );
      })
      .addCase(changeCarLabelAction.rejected, (state, { error }) => {
        state.isLoadingCarAction = false;
        console.log(error.message);
      });
  },
});

export default garageSlice.reducer;
